import React from 'react';
import Icon from '../../../components/AppIcon';

const AnalyticsSidebar = ({ prospects }) => {
  // Calculate analytics
  const totalProspects = prospects?.length || 0;
  const demoScheduled = prospects?.filter(p => p?.status === 'demo-scheduled')?.length || 0;
  const inNegotiation = prospects?.filter(p => p?.status === 'in-negotiation')?.length || 0;
  const closedWon = prospects?.filter(p => p?.status === 'closed-won')?.length || 0;

  const conversionRate = totalProspects > 0 ? ((closedWon / totalProspects) * 100)?.toFixed(1) : 0;
  
  const totalRevenue = prospects
    ?.filter(p => p?.status === 'closed-won')
    ?.reduce((sum, p) => sum + (p?.dealValue || 0), 0);

  const pipelineValue = prospects
    ?.filter(p => p?.status !== 'closed-won')
    ?.reduce((sum, p) => sum + (p?.dealValue || 0), 0);

  const avgDealCycle = prospects?.length > 0 ? 18 : 0; // Mock calculation

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  const stats = [
    {
      label: 'Conversion Rate',
      value: `${conversionRate}%`,
      icon: 'TrendingUp',
      color: 'text-success',
      bg: 'bg-success/10'
    },
    {
      label: 'Avg Deal Cycle',
      value: `${avgDealCycle} days`,
      icon: 'Clock',
      color: 'text-primary',
      bg: 'bg-primary/10'
    },
    {
      label: 'Total Revenue',
      value: formatCurrency(totalRevenue),
      icon: 'DollarSign',
      color: 'text-success',
      bg: 'bg-success/10'
    },
    {
      label: 'Pipeline Value',
      value: formatCurrency(pipelineValue),
      icon: 'Target',
      color: 'text-accent',
      bg: 'bg-accent/10'
    }
  ];

  return (
    <div className="w-80 flex-shrink-0 space-y-4">
      {/* Analytics Header */}
      <div className="bg-card rounded-2xl border border-border p-4">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="BarChart3" size={20} className="text-primary" />
          <h3 className="font-semibold text-foreground">Analytics</h3>
        </div>

        {/* Stats Grid */}
        <div className="space-y-3">
          {stats?.map((stat, index) => (
            <div key={index} className={`p-3 rounded-xl ${stat?.bg}`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon name={stat?.icon} size={16} className={stat?.color} />
                <span className="text-xs text-muted-foreground">{stat?.label}</span>
              </div>
              <p className={`text-lg font-bold ${stat?.color}`}>{stat?.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Stage Distribution */}
      <div className="bg-card rounded-2xl border border-border p-4">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="PieChart" size={20} className="text-primary" />
          <h3 className="font-semibold text-foreground">Stage Distribution</h3>
        </div>

        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-foreground">Demo Scheduled</span>
              <span className="text-sm font-medium text-primary">{demoScheduled}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all"
                style={{ width: `${totalProspects > 0 ? (demoScheduled / totalProspects) * 100 : 0}%` }}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-foreground">In Negotiation</span>
              <span className="text-sm font-medium text-secondary">{inNegotiation}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-secondary h-2 rounded-full transition-all"
                style={{ width: `${totalProspects > 0 ? (inNegotiation / totalProspects) * 100 : 0}%` }}
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-foreground">Closed-Won</span>
              <span className="text-sm font-medium text-success">{closedWon}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-success h-2 rounded-full transition-all"
                style={{ width: `${totalProspects > 0 ? (closedWon / totalProspects) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-card rounded-2xl border border-border p-4">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Zap" size={20} className="text-primary" />
          <h3 className="font-semibold text-foreground">Quick Actions</h3>
        </div>

        <div className="space-y-2">
          <button className="w-full p-3 bg-muted hover:bg-muted/70 rounded-xl text-left transition-colors flex items-center gap-2">
            <Icon name="Download" size={16} className="text-primary" />
            <span className="text-sm text-foreground">Export Pipeline</span>
          </button>
          <button className="w-full p-3 bg-muted hover:bg-muted/70 rounded-xl text-left transition-colors flex items-center gap-2">
            <Icon name="FileText" size={16} className="text-primary" />
            <span className="text-sm text-foreground">Generate Report</span>
          </button>
          <button className="w-full p-3 bg-muted hover:bg-muted/70 rounded-xl text-left transition-colors flex items-center gap-2">
            <Icon name="Users" size={16} className="text-primary" />
            <span className="text-sm text-foreground">Bulk Reassign</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsSidebar;